import { useFirstAudienceId } from '@/features/audiences/hooks/useFirstAudienceId';
import { useWorkspace } from '@/features/workspaces/hooks';
import { EmailSendingDomain } from '@/models/emailSendingDomains';
import { useCompanyValues } from '@/providers';
import { PrepareConnectorFlowStepResponse, usePrepareConnectorFlowStepMutation } from '@/services/endpoints/workspace-connectors';
import { translateI18nValues } from '@/utils/translationUtil';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DomainOption, extractDomainOptionsFromStep1, isNoAvailableDomainsStep1 } from './domainInfoFlowMapper';
import { mapDomainInfoStep2Response } from './domainInfoFlowApi';
import { useEmailSendingDomainConnector } from './useEmailSendingDomainConnector';

type PrepareStepResult = PrepareConnectorFlowStepResponse['configuration'] extends infer T
  ? T extends { elements?: { params?: infer Params } }
    ? NonNullable<Params>
    : Record<string, unknown>
  : Record<string, unknown>;

type UseEmailSendingDomainsOptions = {
  skip?: boolean;
};

export const useEmailSendingDomains = (options?: UseEmailSendingDomainsOptions) => {
  const skip = options?.skip ?? false;
  const workspace = useWorkspace();
  const { audienceId, isLoading: isAudienceLoading, isAudienceSettled } = useFirstAudienceId();
  const { currentLanguage } = useCompanyValues();
  const { infoFlowId, deleteFlowId, connectorInstanceId, isInfoReady, isLoading: isConnectorLoading } = useEmailSendingDomainConnector();
  const [prepareStep] = usePrepareConnectorFlowStepMutation();

  const [domains, setDomains] = useState<EmailSendingDomain[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasNoAvailableDomains, setHasNoAvailableDomains] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const activeRequestId = useRef(0);

  const missingAudience = isAudienceSettled && !audienceId;

  const fetchDomainDetailForOption = useCallback(
    async (option: DomainOption): Promise<{ domain: EmailSendingDomain; flowParams: Record<string, unknown> }> => {
      if (!infoFlowId || !connectorInstanceId || !audienceId) {
        throw new Error('Missing domain info flow configuration');
      }

      const response = await prepareStep({
        workspaceId: workspace.id,
        flowId: infoFlowId,
        audienceId,
        stepId: 'step2',
        stepData: { domain_id: option.id },
        connectorInstanceId,
      }).unwrap();

      const translatedResponse = translateI18nValues(response, currentLanguage);

      return mapDomainInfoStep2Response(translatedResponse, option.id, option.name);
    },
    [audienceId, connectorInstanceId, currentLanguage, infoFlowId, prepareStep, workspace.id]
  );

  const fetchDomains = useCallback(
    async (isRefetch = false) => {
      if (skip || !isInfoReady || !infoFlowId || !connectorInstanceId || !audienceId) {
        return;
      }

      const requestId = ++activeRequestId.current;

      try {
        if (isRefetch) {
          setIsFetching(true);
        } else {
          setIsLoading(true);
        }
        setIsError(false);

        const step1Response = await prepareStep({
          workspaceId: workspace.id,
          flowId: infoFlowId,
          audienceId,
          stepId: 'step1',
          stepData: {},
          connectorInstanceId,
        }).unwrap();

        if (requestId !== activeRequestId.current) {
          return;
        }

        const translatedStep1 = translateI18nValues(step1Response, currentLanguage);
        const step1Params = translatedStep1?.configuration?.elements?.params as PrepareStepResult;
        const noAvailableDomains = isNoAvailableDomainsStep1(step1Params);
        const domainOptions = extractDomainOptionsFromStep1(step1Params);

        if (noAvailableDomains || domainOptions.length === 0) {
          setDomains([]);
          setHasNoAvailableDomains(noAvailableDomains);
          return;
        }

        setHasNoAvailableDomains(false);

        const detailResults = await Promise.all(domainOptions.map((option) => fetchDomainDetailForOption(option)));

        if (requestId !== activeRequestId.current) {
          return;
        }

        setDomains(detailResults.map((result) => result.domain));
      } catch {
        if (requestId === activeRequestId.current) {
          setIsError(true);
        }
      } finally {
        if (requestId === activeRequestId.current) {
          setIsLoading(false);
          setIsFetching(false);
          setHasFetched(true);
        }
      }
    },
    [audienceId, connectorInstanceId, currentLanguage, fetchDomainDetailForOption, infoFlowId, isInfoReady, prepareStep, skip, workspace.id]
  );

  const refetch = useCallback((): Promise<void> => fetchDomains(true), [fetchDomains]);

  useEffect(() => {
    if (skip) {
      setDomains([]);
      setIsLoading(false);
      setIsFetching(false);
      setIsError(false);
      setHasNoAvailableDomains(false);
      setHasFetched(false);
      return;
    }

    if (isConnectorLoading || isAudienceLoading) {
      return;
    }

    if (missingAudience) {
      setDomains([]);
      setIsLoading(false);
      setIsFetching(false);
      setIsError(false);
      setHasNoAvailableDomains(false);
      setHasFetched(true);
      return;
    }

    if (isInfoReady) {
      void fetchDomains();
      return;
    }

    setIsLoading(false);
    setHasFetched(true);
  }, [fetchDomains, isAudienceLoading, isConnectorLoading, isInfoReady, missingAudience, skip]);

  return {
    domains,
    hasNoAvailableDomains,
    hasFetched,
    missingAudience,
    isLoading: skip ? false : isAudienceLoading || isConnectorLoading || (!missingAudience && (isLoading || !hasFetched)),
    isFetching,
    isError,
    refetch,
    isInfoReady,
    infoFlowId,
    deleteFlowId,
    connectorInstanceId,
  };
};
